/** The map editor's outside world: files, the generator, and starting a game.
 *
 * Three things the editor screen cannot do for itself, kept together because
 * all three are the same act - turning a draft into a real `NewGameSetup` and
 * back - and because each one needs the kernel, the content bundle or the DOM.
 */

import type { ContentRegistry, GameState, NewGameSetup } from "@game/core";
import {
  newGame, parseScenario, persistErrorText, saveScenario, scenarioContentWarning,
  serializeScenario, validateScenarioSetup,
} from "@game/core";
import type { EditorDraft } from "@game/ui";

import { generateMap, toObjectSetups } from "@game/content/mapgen";
import type { MapGenTemplate } from "@game/content/mapgen";
import { compileScenario, compileSetup, draftFromSetup } from "./compile.js";
import { PLAYER_COLOURS, startingHero } from "../game/newgame.js";

export type EditorOutcome =
  | { readonly ok: true; readonly message: string }
  | { readonly ok: false; readonly message: string };

const heroFor = (content: ContentRegistry) =>
  (faction: string): ReturnType<typeof startingHero> => startingHero(content, faction);

/** The draft, compiled and checked, or the reason it is not a map yet. */
export function draftSetup(
  draft: EditorDraft, content: ContentRegistry,
): { readonly ok: true; readonly setup: NewGameSetup } | { readonly ok: false; readonly message: string } {
  let setup: NewGameSetup;
  try {
    setup = compileSetup(draft, content, { heroFor: heroFor(content) });
  } catch (e: unknown) {
    return { ok: false, message: e instanceof Error ? e.message : String(e) };
  }
  // The same gate a map file goes through when it is opened, applied to a map
  // that has not been written yet: an editor that can save a file its own
  // loader would refuse is an editor with two ideas of what a map is.
  const checked = validateScenarioSetup(setup);
  if (!checked.ok) return { ok: false, message: persistErrorText(checked.error) };
  return { ok: true, setup };
}

// -------------------------------------------------------------------- file

export function downloadDraft(
  draft: EditorDraft, content: ContentRegistry, filename: string,
): EditorOutcome {
  let text: string;
  try {
    text = serializeScenario(compileScenario(draft, content, { heroFor: heroFor(content) }));
  } catch (e: unknown) {
    return { ok: false, message: `Could not write the map: ${e instanceof Error ? e.message : String(e)}` };
  }
  const blob = new Blob([text], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => { URL.revokeObjectURL(url); }, 0);
  return { ok: true, message: `Saved ${filename} (${String(Math.round(text.length / 1024))} KiB).` };
}

export type OpenOutcome =
  | { readonly ok: true; readonly draft: EditorDraft; readonly warning: string | null }
  | { readonly ok: false; readonly message: string };

export async function openScenarioFile(
  file: Blob, content: ContentRegistry,
): Promise<OpenOutcome> {
  let text: string;
  try {
    text = await file.text();
  } catch (e: unknown) {
    return { ok: false, message: `Could not read the file: ${e instanceof Error ? e.message : String(e)}` };
  }
  const parsed = parseScenario(text);
  if (!parsed.ok) return { ok: false, message: persistErrorText(parsed.error) };
  return {
    ok: true,
    draft: draftFromSetup(parsed.value.setup, {
      name: parsed.value.meta.name,
      description: parsed.value.meta.description,
      author: parsed.value.meta.author,
    }),
    warning: scenarioContentWarning(parsed.value, content),
  };
}

/** The map file as text, for a caller that would rather not touch the DOM. */
export const draftText = (draft: EditorDraft, content: ContentRegistry): string =>
  saveScenario(
    compileSetup(draft, content, { heroFor: heroFor(content) }),
    { name: draft.meta.name, description: draft.meta.description, author: draft.meta.author },
    content.contentVersion,
  );

// --------------------------------------------------------------- generator

/**
 * Fill a draft's board from the random generator.
 *
 * The point of this is the workflow the classic's editor never had: generate a board,
 * then fix by hand the three things the generator got wrong. So it replaces the
 * terrain, the objects and the starts, and keeps the draft's own name - the
 * author has been typing it.
 */
export function generateIntoDraft(
  draft: EditorDraft, content: ContentRegistry, seed: number,
  template?: MapGenTemplate | null,
): EditorDraft {
  const players = Math.max(2, Math.min(8, draft.players.filter((p) => p.kind !== "none").length));
  const size = template?.size ?? draft.width;
  const gen = generateMap(seed, {
    ...(template?.options ?? {}),
    content,
    width: size,
    height: size,
    players,
  });

  const takenHeroes = new Set<string>();
  const setup: NewGameSetup = {
    rootSeed: seed,
    map: gen.map,
    players: gen.startingPositions.map((s, i) => {
      const town = gen.towns[s.town];
      const hero = startingHero(content, s.faction as string, seed + i * 31, takenHeroes);
      takenHeroes.add(hero as string);
      return {
        id: (draft.players[i]?.id ?? `p${String(i + 1)}`) as never,
        kind: draft.players[i]?.kind ?? (i === 0 ? "human" : "ai"),
        colour: PLAYER_COLOURS[i] ?? "red",
        faction: s.faction,
        towns: town === undefined
          ? []
          : [{ faction: s.faction, x: town.x, y: town.y, z: town.z }],
        heroes: [{
          def: hero,
          x: s.heroX, y: s.heroY, z: s.heroZ,
        }],
      };
    }),
    objects: toObjectSetups(gen),
    victory: draft.victory,
  };

  return draftFromSetup(setup, draft.meta);
}

// ------------------------------------------------------------------- play

export function buildGame(
  draft: EditorDraft, content: ContentRegistry,
): { readonly ok: true; readonly state: GameState } | { readonly ok: false; readonly message: string } {
  const setup = draftSetup(draft, content);
  if (!setup.ok) return setup;
  try {
    return { ok: true, state: newGame(setup.setup, content) };
  } catch (e: unknown) {
    return {
      ok: false,
      message: `The kernel refused this map: ${e instanceof Error ? e.message : String(e)}`,
    };
  }
}
